import React from 'react';
import { Check, ChevronRight, Dumbbell, Moon, Plus } from 'lucide-react';
import { useDiaDeHoy } from '../../hooks/useDiaDeHoy';
import { objetivoDelDia } from '../../lib/estadoMacros';
import { num0 } from '../../lib/numeros';
import { MARGEN } from '../../lib/exceso';

const NOMBRE = { P: 'Proteína', H: 'Carbos', G: 'Grasas' };

const fechaLarga = (fecha) => new Date(`${fecha}T12:00:00`)
    .toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

const linea = (m, sinGrasa) => [
    `${num0(m?.P || 0)}P`, `${num0(m?.H || 0)}H`, ...(sinGrasa ? [] : [`${num0(m?.G || 0)}G`]),
].join(' · ');

// EL ESTADO DE CADA COMIDA, CON SU ICONO (handoff «Mi semana», 16-10) y con la regla de color
// de siempre (Enrique: «mantén la actual»): verde lo cuadrado, naranja lo que se pasa y sin
// color lo que falta, que no es un error sino algo por hacer. El estado es el de siempre
// (`useDiaDeHoy`, la misma vara que Nutrición).
const ESTADO = {
    cuadrada: { texto: 'Cuadrada', clase: 'text-ok', icono: 'bg-ok text-[#141414]', dentro: <Check className="w-3.5 h-3.5" strokeWidth={3.5} /> },
    falta: { texto: 'Le falta', clase: 'text-foreground/80', icono: 'bg-foreground/85 text-background', dentro: <span className="text-[13px] font-black leading-none">!</span> },
    sobra: { texto: 'Se pasa', clase: 'text-pasado', icono: 'bg-pasado text-white', dentro: <span className="text-[13px] font-black leading-none">↑</span> },
};

// LOS MACROS DEL DÍA, SIN UN COLOR POR MACRO (handoff, 16-10): el rojo, el amarillo y el
// turquesa chocaban con los colores de estado. La barra va en blanco mientras no llega, en
// verde dentro del margen de siempre (los 4 g de `lib/exceso`) y en naranja si se pasa.
const MacroDelDia = ({ m, creado, objetivo }) => {
    const meta = objetivo?.[m] || 0;
    const hay = creado?.[m] || 0;
    const pct = meta > 0 ? Math.min(100, Math.round((hay / meta) * 100)) : 0;
    const desvio = hay - meta;
    const relleno = meta > 0 && desvio > MARGEN ? 'bg-pasado'
        : meta > 0 && Math.abs(desvio) <= MARGEN ? 'bg-ok' : 'bg-foreground';
    return (
        <div className="min-w-0" data-testid={`dia-elegido-macro-${m}`}>
            <p className="text-xs text-muted-foreground truncate">{NOMBRE[m]}</p>
            <p className="mt-1 whitespace-nowrap">
                <span className="font-heading text-[21px] font-bold text-foreground">{num0(hay)}</span>
                <span className="text-[13px] text-muted-foreground"> / {num0(meta)} g</span>
            </p>
            <div className="h-1 rounded-full bg-muted overflow-hidden mt-1.5">
                <div className={`h-full rounded-full transition-all duration-200 ${relleno}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const DiaElegido = ({ api, userId, dia, dieta, navigate }) => {
    const { tomas } = useDiaDeHoy({
        api, userId, fecha: dia.fecha, dieta,
        objetivo: objetivoDelDia(dieta?.objetivo_comidas), activo: dieta !== undefined,
    });
    const descanso = dia.tipo_dia === 'descanso';
    const ir = (clave) => navigate(`/dashboard/nutrition?date=${dia.fecha}&comida=${clave}`);

    return (
        <section className="rounded-[18px] border border-border bg-card px-3.5 pt-4 pb-1.5 sm:px-5 space-y-3.5" data-testid="mi-semana-dia-elegido">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-[22px] font-bold leading-tight text-foreground first-letter:uppercase">{fechaLarga(dia.fecha)}</h2>
                {dia.tipo_dia && (
                    <span className={`inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-bold whitespace-nowrap
                        ${descanso ? 'text-sky-200' : 'text-brand-light'}`}
                        data-testid="dia-elegido-tipo">
                        {descanso ? <Moon className="w-3.5 h-3.5" /> : <Dumbbell className="w-3.5 h-3.5" />}
                        {descanso ? 'Descanso' : (dia.entreno?.nombre ? `Entreno · ${dia.entreno.nombre}` : 'Entreno')}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="dia-elegido-macros">
                {['P', 'H', 'G'].map((m) => (
                    <MacroDelDia key={m} m={m} creado={dia.macros} objetivo={dia.objetivo} />
                ))}
            </div>

            {/* LAS COMIDAS, EN FILAS QUE SE TOCAN ENTERAS (handoff, 16-10): la fila y su botón
                llevan al mismo sitio, la comida en Nutrición. */}
            <div className="divide-y divide-border border-t border-border" data-testid="dia-elegido-comidas">
                {dieta === undefined ? (
                    [0, 1, 2].map((i) => <div key={i} className="h-14 my-1.5 rounded-xl bg-muted animate-pulse" />)
                ) : tomas.map((t) => {
                    const est = t.tieneAlimentos ? ESTADO[t.estado] : null;
                    return (
                        <button key={t.clave} type="button" onClick={() => ir(t.clave)} data-testid={`dia-elegido-${t.clave}`}
                            className="w-full flex items-center gap-3 py-3 text-left hover:bg-white/[0.02] transition-colors">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                est ? est.icono : t.tieneAlimentos ? 'bg-foreground/85 text-background' : 'border-[1.5px] border-white/25'}`}>
                                {est?.dentro || null}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-[15px] font-semibold text-foreground">{t.nombre}</span>
                                    {est && <span data-testid="estado-de-la-comida" className={`text-xs font-semibold ${est.clase}`}>{est.texto}</span>}
                                </span>
                                <span className="block text-xs text-muted-foreground font-data mt-0.5 truncate">
                                    {t.tieneAlimentos ? linea(t.montado, t.esPeri)
                                        : t.objetivo ? `Objetivo ${linea(t.objetivo, t.esPeri)}` : 'Sin crear'}
                                </span>
                            </span>
                            {t.tieneAlimentos ? (
                                <span data-testid={`ver-${t.clave}`}
                                    className="flex-shrink-0 inline-flex items-center gap-0.5 rounded-[10px] bg-muted px-3 py-2 text-[13px] font-bold text-foreground">
                                    Ver <ChevronRight className="w-4 h-4" />
                                </span>
                            ) : (
                                <span data-testid={`crear-${t.clave}`}
                                    className="flex-shrink-0 inline-flex items-center gap-1 rounded-[10px] bg-brand px-3 py-2 text-[13px] font-bold text-[#141414]">
                                    <Plus className="w-4 h-4" /> Crear
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

export default DiaElegido;
